import React, { useEffect, useState, useCallback } from 'react';
import {
  SelectOption,
  IconPlus,
  IconSearch,
  OrangeButton,
  IconFilter,
  IconSmallArrow,
  SmallButton,
  IconFormMinus,
  IconFormPlus,
  CustomTable,
  IconReset,
  DownloadClientsCSV,
} from 'components';
import SlhSelect from 'components/slhSelect';
import { COLORS } from 'utils/colors';
import dayjs from 'dayjs';
import DefaultAvatar from 'assets/images/default-avatar.png';
import { endpoints } from 'api/endpoints';
import classNames from 'classnames';
import { getRequestData } from 'api/api';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CLIENT_STATUS, DATE_FORMAT, ROLE_TYPES } from 'api/constants';
import { Input } from 'antd';

export const AdminClients = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { parentId } = location.state || {};
  const [clientsList, setClientsList] = useState();
  const [allPartners, setAllPartners] = useState();
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(parentId || '');
  const [searchWord, setSearchWord] = useState(null);

  const columns = [
    {
      title: 'Resident',
      dataIndex: 'client',
      width: 300,
      render: (_, record) => (
        <div className="flex items-center gap-x-1">
          <img
            className="w-[30px] h-[30px] rounded-full"
            src={record.clientPhoto ? record.clientPhoto : DefaultAvatar}
            alt="Resident"
          />
          <p className="text-14 ml-1">{record.client}</p>
          {record.isHaveNotCompletedTask ? (
            <IconFormMinus className="min-w-4 min-h-4" />
          ) : (
            <IconFormPlus className="min-w-4 min-h-4" />
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      render: (status, record) => {
        const statusAbbr = status.slice(0, 1).toUpperCase();
        return (
          <div className="flex items-center gap-x-3">
            <p className="w-6 h-6 rounded-full bg-white shadow-block-shadow flex justify-center items-center">
              <span
                className={classNames(
                  'uppercase text-xl leading-5 font-medium',
                  { 'text-blue': statusAbbr === 'N' },
                  { 'text-orange': statusAbbr === 'I' },
                  { 'text-green': statusAbbr === 'C' },
                  { 'text-red': statusAbbr === 'R' },
                )}
              >
                {statusAbbr === 'R' ? 'E' : statusAbbr}
              </span>
            </p>
            <p className="text-14 uppercase font-medium">W{record.week ? record.week : 0}</p>
          </div>
        );
      },
    },
    {
      title: 'SLH',
      width: 300,
      dataIndex: 'partner',
      render: (_, record) => (
        <Link
          to={`/partner/view/${record.partnerID}`}
          className="text-14 font-medium text-ellipsis w-[300px] overflow-hidden whitespace-nowrap pr-4"
        >
          {record.partner}
        </Link>
      ),
    },
    {
      title: 'Scholarship Start Date',
      dataIndex: 'startDate',
      width: 120,
      showSorterTooltip: {
        target: 'sorter-icon',
      },
      render: (text) => dayjs(text).format(DATE_FORMAT),
    },
    {
      title: 'Action',
      width: 100,
      dataIndex: 'action',
      className: 'action',
      render: (_, record) => (
        <SmallButton onClick={() => navigate(`/client/${record.userID}`)} label="Details" />
      ),
    },
  ];

  const fetchClients = useCallback(async (params) => {
    const { data } = await getRequestData(endpoints.getClients(params), ROLE_TYPES.admin);
    if (data) {
      const clientsData = {
        currentPage: data.currentPage,
        pageCount: data.pageCount,
        pageSize: data.pageSize,
        clients: data.results.map((client) => ({
          client: `${client.firstname} ${client.lastname}`,
          clientID: client.client_id,
          clientPhoto: client.avatar,
          status: client.status,
          partner: client.partner,
          partnerID: client.partner_id,
          startDate: client.start_date,
          userID: client.user_id,
          week: client.week,
          isHaveNotCompletedTask: client.tasks.some((task) => task.completed === false),
        })),
      };
      setClientsList(clientsData);
    }
  }, []);

  useEffect(() => {
    (async () => {
      const { data } = await getRequestData(endpoints.getAllPartners(), ROLE_TYPES.admin);
      if (data) {
        setAllPartners(data);
      }
    })();
  }, []);

  const handlePaginationChange = (page) => {
    const filters = { page };

    if (selectedStatus) {
      filters.status = selectedStatus;
    }
    if (selectedPartner) {
      filters.partner_ids = selectedPartner;
    }
    if (searchWord) {
      filters.search = searchWord;
    }

    fetchClients(filters);
  };

  const fetchClientsWithFilters = useCallback(
    (status, partner_id, searchQuery) => {
      const filters = {};
      if (status) filters.status = status;
      if (partner_id) filters.partner_ids = partner_id;
      if (searchQuery) filters.search = searchQuery;

      fetchClients(filters);
    },
    [fetchClients],
  );

  useEffect(() => {
    if (selectedPartner) {
      fetchClientsWithFilters(selectedStatus, selectedPartner, searchWord);
    } else {
      fetchClients({ page: 1, pageSize: 10 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchClients, fetchClientsWithFilters, selectedPartner]);

  const handleStatusSelectChange = (status) => {
    setSelectedStatus(status);
    fetchClientsWithFilters(status, selectedPartner, searchWord);
  };

  const handleSLHSelectChange = (id) => {
    setSelectedPartner(id);
    fetchClientsWithFilters(selectedStatus, id, searchWord);
  };

  const handleFiltersReset = () => {
    setSelectedStatus(null);
    setSelectedPartner('');
    setSearchWord(null);
    // restore full partners list when filters are reset
    fetchClients({ page: 1, pageSize: 10 });
  };

  const handleSearch = (e) => {
    const searchTerm = e.target.value;

    if (searchTerm.length > 2 || (searchWord && searchWord.length > searchTerm.length)) {
      fetchClientsWithFilters(selectedStatus, selectedPartner, searchTerm);
    }
    setSearchWord(searchTerm);
  };

  return (
    <div className="flex flex-col w-full gap-y-6">
      <div className="flex justify-between w-full">
        <p className="nunito-title">Residents</p>
        <div className="flex items-center gap-x-6">
          <Input
            className="search w-[300px] text-lightBlue text-16 rounded-ten bg-light"
            type="text"
            value={searchWord}
            placeholder="Search"
            addonBefore={<IconSearch color={COLORS.lightBlue} />}
            onChange={handleSearch}
          />
          <OrangeButton
            onClick={() => navigate('/create-client')}
            label="Add new resident"
            icon={<IconPlus />}
          />
          {clientsList && <DownloadClientsCSV list={clientsList.clients} />}
        </div>
      </div>
      {clientsList && (
        <>
          <div className="flex items-center gap-x-3">
            <div className="flex items-center gap-x-1 mr-3">
              <IconFilter />
              <p className="text-16">Filter</p>
            </div>
            <SelectOption
              onSelect={(value) => handleStatusSelectChange(value)}
              dropdownStyle={{ padding: '16px', background: 'white' }}
              required
              value={selectedStatus}
              suffixIcon={
                <IconSmallArrow color={COLORS.black} className="rotate-[-90deg] w-3 h-3" />
              }
              className="h-[40px] text-base font-medium base-block rounded-lg"
              popupClassName="min-w-[220px]"
              placeholder="Status"
              dropdownRender={(originalNode) => originalNode}
              optionRender={(option) => (
                <div
                  className={`flex items-center px-2 py-1 gap-x-1 text-black rounded-md capitalize border border-color-divider bg-white overflow-hidden ${selectedStatus === option.value ? 'border-orange' : ''}`}
                >
                  <span
                    className={`w-6 h-6  rounded-full border border-orange ${selectedStatus === option.value ? 'border-[6px]' : ''}`}
                  />
                  <p className="font-14 flex-1">{option.label}</p>
                </div>
              )}
              options={[
                {
                  label: <p className="text-14 px-4 py-1.5 rounded-md">In Progress</p>,
                  value: CLIENT_STATUS.in_progress,
                },
                {
                  label: <p className="text-14 px-4 py-1.5 rounded-md">Completed</p>,
                  value: CLIENT_STATUS.completed,
                },
                {
                  label: <p className="text-14 px-4 py-1.5 rounded-md">Ended</p>,
                  value: CLIENT_STATUS.failed,
                },
                {
                  label: <p className="text-14 px-4 py-1.5 rounded-md">Not Started</p>,
                  value: CLIENT_STATUS.not_started,
                },
              ]}
            />
            <SlhSelect
              data={allPartners}
              value={selectedPartner}
              onChange={(id) => handleSLHSelectChange(id)}
            />
            {(selectedPartner || selectedStatus || searchWord) && (
              <SmallButton onClick={handleFiltersReset} label="Reset All" icon={<IconReset />} />
            )}
          </div>
          <CustomTable
            columns={columns}
            data={clientsList.clients}
            pageCount={clientsList.pageCount}
            current={clientsList.currentPage}
            pageSize={clientsList.pageSize}
            onChange={handlePaginationChange}
            rowKey={(record) => record.userID}
          />
        </>
      )}
    </div>
  );
};
